/**
 * Sprint 6.0a — Tool-to-query-key invalidation registry.
 * Sprint 24.12 — extended to close the "chat mutation doesn't refresh the UI" gap.
 *
 * Maps agent tool names to React Query cache keys that should be invalidated when
 * the tool completes. `ToolCallCard` calls `invalidateForTool` on every completed
 * chat tool-result part (since Sprint 6.0a), so this registry IS the in-app chat's
 * "tool → queryKey map" — the mechanism Sprint 24.12's spec proposed as a new
 * `tool-to-query-map.ts`. Rather than stand up a parallel system (and double-
 * invalidate), 24.12 extends this one. Each rule mirrors what the equivalent
 * direct-UI mutation hook already invalidates, so a mutation refreshes the same
 * surfaces whether it came from a form or from chat:
 *   - tasks       → use-task-mutations' invalidateTasks (["agent-tasks"]+["my-tasks"]+brain snapshot)
 *   - equipment   → use-connections' invalidateEquipment (unified + equipment + catalogue)
 *
 * Rules whose tool failed are skipped by the caller (ToolCallCard only invalidates
 * non-error results), so a mutation that didn't land never triggers a refetch.
 *
 * Unknown tools default to mutation (conservative) and fall back to broad keys, so
 * a brand-new mutating tool still refreshes something until it gets an explicit row.
 */

import type { QueryClient } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Read-only tool classification
// ---------------------------------------------------------------------------

// Sprint 24.12 — search_/read_/recommend_/suggest_ are read-only verbs too;
// without them a chat `search_*`/`suggest_*` call hit FALLBACK_KEYS and triggered
// a wasteful workspace-wide refetch. (Appendix C of the 24.12 spec.)
const READ_PREFIXES = ["get_", "list_", "find_", "search_", "read_", "recommend_", "suggest_"];

const READ_TOOLS = new Set([
  "get_workspace_info",
  "get_workspace_context",
  "list_tree",
  "list_children",
  "list_entity_types",
  "list_files",
  "list_tasks",
  "list_needs_you",
  "list_workspace_tasks",
  "list_task_references",
  "list_task_history",
  "get_task",
  "get_agent_snapshot",
  "list_agent_routines",
  "get_routine",
  "list_activities",
  "list_activities_for",
  "get_activity",
  "list_views",
  "get_ai_usage",
  "get_node",
  "get_file",
  "get_view",
  "get_connection",
  "list_connections",
  // Sprint 24.12 — non-prefixed read-only tools (Appendix C).
  "surface_relevant_memories",
  "check_task_against_learnings",
  "compose_retrospective",
  "verify_references",
  "infer_oauth_config",
]);

export function isMutatingTool(toolName: string): boolean {
  if (READ_TOOLS.has(toolName)) return false;
  if (READ_PREFIXES.some((p) => toolName.startsWith(p))) return false;
  return true;
}

export function extractEntityType(toolName: string): string | undefined {
  const m = toolName.match(/^(?:create|update|archive|restore)_(.+)$/);
  const candidate = m?.[1];
  if (!candidate) return undefined;
  // Internal primitives that LOOK like entity slugs but have dedicated rules /
  // query keys — never treat them as a dynamic entity type.
  const BUILTIN = new Set([
    "node",
    "memory",
    "agent",
    "task",
    "routine",
    "activity",
    "entity_type",
    "folder",
    "view",
    "wrapped_api",
    // Sprint 24.12 additions — each has an explicit rule below.
    "chat_session",
    "prompt",
    "page",
    "file",
  ]);
  if (BUILTIN.has(candidate)) return undefined;
  return candidate.replace(/_/g, "-");
}

// ---------------------------------------------------------------------------
// Invalidation rules
// ---------------------------------------------------------------------------

interface InvalidationRule {
  match: string | RegExp;
  queryKeys: readonly (string | readonly string[])[];
  dynamicKeys?: (toolName: string) => (string | readonly string[])[];
}

const MUTATION_REGISTRY: readonly InvalidationRule[] = [
  // Tasks (Sprint 26.0) — the one work noun. create/update/complete/triage/move/
  // drop/archive/restore/delete. The broad ["agent-tasks"] prefix covers every
  // (nodeId, filter) variant; ["my-tasks"] is the workspace-wide roll-up + badge;
  // the brain snapshot composes the queue/inbox so it must refresh too.
  {
    match: /^(create_task|update_task|complete_task|triage_task|move_task|drop_task|archive_task|restore_task|delete_task)$/,
    queryKeys: [["agent-tasks"], ["my-tasks"], ["agent-brain-snapshot"], ["agent-queue"], ["agent-inbox"], ["needs-you"], ["agent-history"]],
  },

  // Task references (Sprint 26.0) — refresh tasks + page lists (mirrors
  // use-agent-tasks' invalidateReferences).
  {
    match: /^(add_task_reference|remove_task_reference)$/,
    queryKeys: [["agent-tasks"], ["agent-brain-snapshot"], ["workspace-pages"], ["child-nodes"]],
  },

  // Memory operations
  {
    match: "create_memory",
    queryKeys: [["library-children"], ["library-tree"], ["workspace"], ["agent-memories"]],
  },

  // Node updates (covers memory edits, agent config changes)
  {
    match: "update_node",
    queryKeys: [["library-node"], ["library-children"], ["library-tree"], ["workspace"]],
  },

  // Archive/restore any node (includes memory + page deletion)
  {
    match: /^(archive_node|restore_node|restore_entity)$/,
    queryKeys: [["library-children"], ["library-tree"], ["workspace"], ["library-node"], ["agent-memories"], ["workspace-pages"]],
  },

  // Delegation (Sprint 24.8) — a sub-agent run may create/affect tasks.
  {
    match: "delegate_to_agent",
    queryKeys: [["agent-tasks"], ["my-tasks"], ["agent-brain-snapshot"]],
  },

  // Routines (Sprint 14)
  {
    match: /^(create_routine|update_routine|archive_routine|restore_routine|delete_routine|record_routine_run|promote_routine_prompt_to_shared)$/,
    queryKeys: [["agent-routines"], ["routine"]],
  },

  // Activity (Sprint 14)
  {
    match: /^(create_activity|update_activity|archive_activity|restore_activity|delete_activity)$/,
    queryKeys: [["activities"], ["row-detail"]],
  },

  // File operations
  {
    match: /^(upload_file|delete_file|restore_file)$/,
    queryKeys: [["files"]],
  },

  // Tree structure changes
  {
    match: /^(promote_row|demote_row|promote_to_agent|demote_to_folder|move_node|create_folder)$/,
    queryKeys: [["library-children"], ["library-tree"], ["workspace"], ["library-node"], ["agent-hierarchy"], ["entities"]],
  },

  // Agent creation
  {
    match: "create_agent",
    queryKeys: [["library-children"], ["library-tree"], ["workspace"], ["agent-hierarchy"]],
  },

  // Entity type schema changes — ALSO the MCP endpoint catalogue + tree (Sprint
  // 24.12: per-source endpoint counts shift when a type is added/removed).
  {
    match: /^(create_entity_type|update_entity_type|delete_entity_type|unlink_entity_type_references)$/,
    queryKeys: [["entity-types"], ["workspace"], ["admin", "workspace-status"], ["mcp-endpoints"], ["library-tree"]],
  },

  // Pages (Sprint 5o) — surfaced via workspace-pages / child-nodes, not entities.
  {
    match: /^(create_page|update_page)$/,
    queryKeys: [["workspace-pages"], ["child-nodes"], ["library-tree"], ["library-children"]],
  },

  // Prompts (Sprint 11) — mirrors use-prompt-mutations' invalidatePromptCaches.
  {
    match: /^(create_prompt|update_prompt)$/,
    queryKeys: [["prompts-in-scope"], ["all-prompts"], ["mcp-prompts"], ["library-tree"]],
  },

  // Chat sessions (Sprint 22)
  {
    match: /^(archive_chat_session|delete_chat_session)$/,
    queryKeys: [["chat-sessions"]],
  },

  // Workspace members (Sprint 7)
  {
    match: /^(invite_user|cancel_invitation|resend_invitation|remove_user|update_member_role)$/,
    queryKeys: [["workspace-members"]],
  },

  // View operations
  {
    match: /^(create_view|update_view|archive_view)$/,
    queryKeys: [["views"]],
  },

  // Agent equipment (Sprint 24) — the Workforce "Equipped with" panel, the
  // unified Connections list, "agents equipped with X", AND the endpoint
  // catalogue (mirrors use-connections' invalidateEquipment + the catalogue).
  {
    match: /^(equip_agent_with_connection|unequip_agent_from_connection)$/,
    queryKeys: [["connections-unified"], ["agent-equipment"], ["agents-equipped-with"], ["mcp-endpoints"]],
  },

  // Connection CRUD (Sprint 7.1 → 24 / 24.5) — the unified surface + catalogue.
  {
    match: /^(add_connection|remove_connection|test_connection|register_from_template)$/,
    queryKeys: [["connections"], ["connections-unified"], ["mcp-endpoints"]],
  },

  // Wrapped API operations (Sprint 16) — config, mappings, credentials, breaker,
  // and spec refresh all refresh the wrapped-API caches; add/remove/refresh also
  // change the per-wrapper `/mcp/w/<slug>` endpoints.
  {
    match: /^(add_wrapped_api|update_wrapped_api|remove_wrapped_api|refresh_wrapped_api_spec|set_wrapped_api_credential|set_wrapped_api_service_credential|add_wrapped_api_mapping|update_wrapped_api_mapping|remove_wrapped_api_mapping|reset_wrapped_api_circuit_breaker)$/,
    queryKeys: [["wrapped-apis"], ["wrapped-api"], ["mcp-endpoints"]],
  },

  // Workspace-level changes (update_workspace_name syncs name to the root node)
  {
    match: /^(set_workspace_context|set_workspace_instructions|update_workspace_name|update_workspace_settings|delete_workspace_context|update_preferred_persona)$/,
    queryKeys: [["workspace"], ["workspace-context"], ["library-node"], ["library-children"], ["agent-hierarchy"]],
  },

  // Entity CRUD (dynamic) — kept LAST. Explicit rules above already handle the
  // built-in primitives; this scopes the refetch to the affected entity type only
  // (no broad ["entities"] nuke). BUILTIN names resolve to undefined → no-op.
  {
    match: /^(create|update|archive|restore)_/,
    queryKeys: [],
    dynamicKeys: (toolName: string) => {
      const slug = extractEntityType(toolName);
      return slug ? [["entities", slug], ["entity", slug], ["row-detail", slug]] : [];
    },
  },
];

// Broad keys invalidated for unknown mutating tools
const FALLBACK_KEYS: readonly (readonly string[])[] = [
  ["workspace"],
  ["library-children"],
  ["agent-hierarchy"],
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function invalidateForTool(toolName: string, qc: QueryClient): void {
  if (!isMutatingTool(toolName)) return;

  let matched = false;
  const invalidated: (string | readonly string[])[] = [];

  for (const rule of MUTATION_REGISTRY) {
    const matches =
      typeof rule.match === "string"
        ? toolName === rule.match
        : rule.match.test(toolName);

    if (!matches) continue;

    const keys: (string | readonly string[])[] = [
      ...rule.queryKeys,
      ...(rule.dynamicKeys ? rule.dynamicKeys(toolName) : []),
    ];
    // A rule with only an empty dynamicKeys result (e.g. the generic entity rule
    // hitting a BUILTIN) didn't actually invalidate anything — don't let it
    // suppress the fallback.
    if (keys.length === 0) continue;
    matched = true;

    for (const key of keys) {
      const queryKey = Array.isArray(key) ? key : [key];
      qc.invalidateQueries({ queryKey });
      invalidated.push(queryKey);
    }
  }

  if (!matched) {
    for (const key of FALLBACK_KEYS) {
      qc.invalidateQueries({ queryKey: [...key] });
      invalidated.push(key);
    }
  }

  // Sprint 24.12 (S24.12.15) — dev-only trace so map drift is visible while
  // working (a mutating tool that only ever hits FALLBACK_KEYS wants a row).
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[24.12] invalidating after", toolName, invalidated);
  }
}
